import React, { useEffect, useMemo, useState, useCallback } from 'react';
import classNames from 'classnames';
import assign from 'lodash/assign';
import cloneDeep from 'lodash/cloneDeep';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import { ApolloQueryResult, QueryResult, useQuery } from '@apollo/client';
import { OfferingStatus, LinkedProductTypes } from '@atlassian/cssxp-ccp-types';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import { Box, Flex, Text } from '@atlaskit/primitives';
import Form, { FormFooter, FormHeader, FormSection, CheckboxField } from '@atlaskit/form';
import { Checkbox } from '@atlaskit/checkbox';
import { ProgressTracker, Stages } from '@atlaskit/progress-tracker';
import SectionMessage from '@atlaskit/section-message';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import { analytics, AnalyticsTrackAction, AnalyticsTrackSubject, PlanType } from '@atlassian/temp-analytics';
import { SignalFxFEPerfStore } from '@atlassian/temp-helper/constants';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { logTimeStamp } from '@atlassian/temp-metal';
import {
  getEntitlementDefaults,
  updateEntitlementPlan,
  getOrderPreviewV2,
  updateEntitlementPlanV2,
} from '@atlassian/temp-proxies';
import {
  EntitlementBaseProduct,
  EntitlementDefaultsRequest,
  EntitlementDefaultsResponse,
  OMSMetadata,
  OrderItemType,
} from '@atlassian/cssxp-types-entitlement';
import {
  CCPPricing,
  PatchOperation,
  PatchRequestOperation,
  PromotionInstance,
  UpdateOrderRequest,
} from '@atlassian/cssxp-typescommon';
import {
  EntitlementEdition,
  getUserTier,
  isOfferingArchived,
  isOfferingAtNotice,
  isOfferingPaid,
  notActivePricingStatus,
} from '@atlassian/cssxp-utils-entitlement';
import { isProductSkipTrialEligible, isStatuspage } from '@atlassian/cssxp-utils-product';
import { getCorrectProgramLevelGlobal, isCollectionProductEntitlement, isLoomProduct } from '@css-xp/ppc-utils';
import {
  CustomPanel,
  Divider,
  ErrorComponents,
  errorNotificationBuilder,
  InflightInvoiceBannerForInvoiceGroup,
  InfoPanel,
  InvoiceGroupSelectItem,
  isDiscretionaryDiscountPromotion,
  NotificationTypes,
  SelectPromotionSelectItem,
  successNotificationBuilder,
  useApplicationContext,
  useFeatureToggle,
  useFormSubmit,
  useNotification,
  useSidebar,
  withApollo,
} from '@css-xp/base';
import {
  currentEntitlementType,
  getChargeElement,
  getParentContainerId,
  CustomOrderEntitlement,
} from '@css-xp/base/entitlement-utils';
import { isTrialEntitlement } from '@css-xp/entitlements-v2';
import { getPartnerSubscriptionsUrl, trackPartnerServiceDeskClick } from '@css-xp/ppc-navigation';
import {
  BillingFrequency,
  EntitlementDetails,
  FetchAvailableOfferingsForEntitlementPpcQuery,
  FetchAvailablePartnerPromotionsForChangeEntitlementPpcQuery,
  FetchAvailablePartnerPromotionsForChangeEntitlementPpcQueryVariables,
  FetchTransactionAccountForChangeSubscriptionPaneQuery,
  FetchTransactionAccountForChangeSubscriptionPaneQueryVariables,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  Offering,
  OrderItemRequestType,
  PricingType,
  PromoCodeDetails,
  Promotion,
  RelationshipPathType,
} from '@css-xp/graphtypes/src/generated-types-new';
import { legalLinks, useConsentHub } from '@css-xp/ppc-consent-hub';
import { EditSubscriptionTermsAndConditions } from '../../components/terms-and-conditions/terms-and-conditions-details';
import { DEFAULT_INFLIGHT_BANNER_MESSAGE, HelpContent } from '@css-xp/ppc-common';
import { useFetchEccFromContactService } from '@css-xp/contact-resources';
import { PARTNER_DISCOUNT_TYPE, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { ManagePlan } from './manage-plan-perf-v2';
import { ManagePromotion } from './manage-promotion';
import {
  fetchAvailableOfferingsForEntitlement,
  fetchAvailablePartnerPromotionsForChangeEntitlementPPC,
  fetchTransactionAccountForChangeSubscriptionPane,
} from './queries';
import { BehaviourAtEndOfTrial, ReviewChange } from './review-change-perf-v2';
import { buildMessage } from './utils/message-builder';
import { useFetchLinkedEntitlementsPPCQuery } from '../../../hooks/use-fetch-linked-entitlements-ppc-query';
import { fetchLinkedEntitlementBillingProfile } from '../../../queries/fetch-linked-entitlement-billing-profile';
import { useChangeSubscriptionForCollectionProduct } from './hooks/useChangeSubscriptionForCollectionProduct';
import { getAvailableActiveOfferingOptions } from './utils/change-subscription-pane-utils';
import './change-subscription-pane.scss';

enum TrackerState {
  VISITED = 'visited',
  CURRENT = 'current',
  UNVISITED = 'unvisited',
}

interface FormTrackerStep {
  PLAN: number;
  PROMOTIONS?: number;
  REVIEW: number;
}

interface CustomValues {
  value: string;
  label: string;
}

export interface DefaultEntitlementData {
  response?: {
    [key: string]: {
      [key: string]: EntitlementDefaultsResponse;
    };
  };
  error?: any;
  loading?: boolean;
}

export enum FormTrackerStepNames {
  PLAN = 'PLAN',
  PROMOTIONS = 'PROMOTIONS',
  REVIEW = 'REVIEW',
}

interface OrderItemResponse {
  orderItemId: string;
  processingInfo: {
    status: string;
  };
  errors: {
    errorCode: string;
    message: string;
    field: string;
  };
}

interface Props {
  entitlement: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  initialFormTrackerStep?: keyof typeof FormTrackerStepNames;
  formState?: Partial<FormData>;
  showSkipTrialCheckbox?: boolean;
  txaId: string;
  hideProgressTracker?: boolean;
  isEligibleForPremiumTrial?: boolean;
  isEligibleForAdvancedTrial?: boolean;
}

export interface FormData {
  currentPlan?: string;
  newPlan?: {
    label: string;
    value: string;
  };
  units?: string;
  billingFrequency?: BillingFrequency;
  pricing?: string;
  promotionsToAdd?: SelectPromotionSelectItem[];
  promotionsToRemove?: SelectPromotionSelectItem[];
  schedulePromotionsToRemove?: SelectPromotionSelectItem[];
  invoiceGroupId?: InvoiceGroupSelectItem;
  notes?: string;
  customDuration?: CustomValues[];
  customIterations?: string[];
  customReason?: CustomValues[];
  customValue?: string[];
  skipTrial?: boolean;
  comments?: string;
  pricePlan?: {
    label: string;
    value: string;
    chargeElement: string;
    chargeType?: string;
    pricingPlanType?: string;
  };
  nextBillingAnchorTimestamp?: number;
  promoCode?: string;
  pricePlanName?: string;
  userTier?: {
    label: string;
    value: string;
  };
  consentTaken?: boolean;
  // For promoCode which comes as promoCode-0
  'promoCode-0'?: string;
}

enum OrderType {
  AMENDMENT_ORDER = 'AMENDMENT_ORDER',
}

interface DiscretionaryEstimate {
  error?: any;
  discretionaryDiscountAmount?: number;
  loading: boolean;
  isDisabled?: boolean;
}

// This check is need for the time being until CCP supports scheduled operations
const getFormError = (errorMsg: string, orderType?: string): React.ReactElement | string => {
  if (
    (orderType === OrderItemRequestType.CANCELLATION_ORDER || orderType === OrderItemRequestType.AMENDMENT_ORDER) &&
    errorMsg.includes('Entitlement relationship validation failed')
  ) {
    return (
      <>
        {`This subscription can't be ${
          orderType === OrderItemRequestType.CANCELLATION_ORDER ? 'canceled' : 'changed'
        } just yet. You need to cancel all linked items to this subscription first.`}
        <br />
        {errorMsg}
      </>
    );
  }
  return errorMsg;
};

const stagesForProduct = ['Plan', 'Review'];
const stagesForApp = ['Plan', 'Discounts', 'Review'];

export const FormTrackerStepProduct = {
  [FormTrackerStepNames.PLAN]: 0,
  [FormTrackerStepNames.REVIEW]: 1,
};

export const FormTrackerStepApp = {
  [FormTrackerStepNames.PLAN]: 0,
  [FormTrackerStepNames.PROMOTIONS]: 1,
  [FormTrackerStepNames.REVIEW]: 2,
};

export const ChangeSubscriptionPane = withApollo(
  ({
    entitlement,
    refetch,
    initialFormTrackerStep = FormTrackerStepNames.PLAN,
    formState,
    showSkipTrialCheckbox,
    txaId,
    hideProgressTracker,
    isEligibleForPremiumTrial,
    isEligibleForAdvancedTrial,
  }: Props) => {
    const FormTrackerStep: FormTrackerStep = entitlement?.isApp ? FormTrackerStepApp : FormTrackerStepProduct;
    let showDiscretionaryEstimateError = false;
    const isStatusPage = entitlement.baseProduct === EntitlementBaseProduct.STATUS_PAGE;
    const isTrial = isTrialEntitlement(entitlement, isStatusPage);
    const isCurrentEntitlementFree = !isOfferingPaid(entitlement);
    const { getECCDetails, eccDetailsLoading } = useFetchEccFromContactService();
    const [isContactsAvailable, setIsContactsAvailable] = useState<boolean>(false);
    const [hasOptedForUpgradeTrial, setHasOptedForUpgradeTrial] = React.useState<boolean>(false); // evaluates to TRUE only with respect to UI actions of selecting premium with trial .
    const [grandTotal, setGrandTotal] = React.useState<number>();
    const [pricingPlanId, setPricingPlanId] = React.useState<string | null>(null);
    // const [isDisplayEndNoCostPremiumBanner, setIsDisplayEndNoCostPremiumBanner] = React.useState<boolean>(false);
    const isExistingAnnualPlanForJCA =
      entitlement?.billingFrequency === BillingFrequency.ANNUAL && !isStatuspage(entitlement?.productId);

    const activePromotions = entitlement?.promotions || [];
    const activePromoCodePromotion = activePromotions?.find((promotion) => promotion?.type === 'PROMO_CODE') as any;
    const [currentProgressIndex, setCurrentProgressIndex] = React.useState<number>(
      // @ts-ignore:next-line
      FormTrackerStep[initialFormTrackerStep] ?? 0
    );
    const [currFormState, setCurrFormState] = React.useState<FormData>({
      invoiceGroupId: {
        invoiceGroup: {},
        value: entitlement?.billingGroupId as string,
      },
      ...formState,
    });

    React.useEffect(() => {
      (async () => {
        const invoiceGroupId = currFormState?.invoiceGroupId?.value;
        if (invoiceGroupId && txaId) {
          const data = await getECCDetails(invoiceGroupId, txaId);
          if (data?.length > 0) {
            setIsContactsAvailable(true);
          } else {
            setIsContactsAvailable(false);
          }
        }
      })();
    }, [currFormState?.invoiceGroupId?.value, txaId]);

    const [billingFrequency, setBillingFrequency] = React.useState<BillingFrequency>(
      defaultTo(
        currFormState?.billingFrequency,
        defaultTo(entitlement?.pricingPlan?.billingFrequency, BillingFrequency.MONTHLY)
      )
    );
    const [availableOfferingsData, setAvailableOfferings] = React.useState<
      Partial<QueryResult<FetchAvailableOfferingsForEntitlementPpcQuery>>
    >({ loading: true });
    const [availablePromotions, setAvailablePromotions] = React.useState<Promotion[]>(
      defaultTo(entitlement?.availablePromotions, []) as Promotion[]
    );
    const [cachedPromotions, setCachedPromotions] = React.useState<any>({});
    const [isSkipTrialChecked, setIsSkipTrialChecked] = React.useState(
      !isEligibleForPremiumTrial && !isEligibleForAdvancedTrial && (!isTrial || !!currFormState?.skipTrial)
    );
    const [isSkipTrialDisabled, setIsSkipTrialDisabled] = React.useState(!isExistingAnnualPlanForJCA || !isTrial); // make it default as true once we suport edition change for annual evergreen
    const [productSkipTrialEligible, setProductSkipTrialEligible] = React.useState(
      isProductSkipTrialEligible(defaultTo(entitlement?.offering?.productKey, ''), !!entitlement?.isApp)
    );
    const [isImmediateFlag, setIsImmediateFlag] = React.useState(false);
    const [selectedOfferingId, setSelectedOfferingId] = useState<string>();
    const [discretionaryEstimate, setDiscretionaryEstimate] = React.useState<Partial<DiscretionaryEstimate>>({
      loading: false,
    });
    // Billing Profile with missing payment method or ship to address are invalid .
    const [isBillingProfileInValid, setIsBillingProfileInValid] = React.useState<boolean>(false);
    const [defaultPricingPlan, evaluatePricingPlan] = React.useState<string>('');
    const [defaultEntitlementData, setDefaultEntitlementData] = React.useState<Partial<DefaultEntitlementData>>({});
    const [validPromoCodeData, setValidPromoCodeData] = React.useState<Partial<PromoCodeDetails[]>>([]);
    const [activePromoCodeData, setActivePromoCodeData] = React.useState<Partial<PromoCodeDetails[]>>(
      activePromoCodePromotion ? [activePromoCodePromotion] : []
    );

    const [agreedToTnC, setAgreedToTnC] = React.useState(true);
    const [moreThanOnePartnerPromotionsAvailable, setMoreThanOnePartnerPromotionsAvailable] = React.useState(false);

    const { updateCustomerConsent } = useConsentHub();
    const [isEnabled] = useFeatureToggle();
    const isItALoomProduct = isLoomProduct(entitlement.productId);
    const isLoomSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_LOOM_SUPPORT) && isItALoomProduct;
    const isOmsV2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MANAGE_SUBSCRIPTION_OMS_V2);
    const isCompassPremiumEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_COMPASS_PREMIUM);
    const isJPDPremiumOfferingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_PREMIUM_OFFERING);
    const isJPDAsJiraFamilyEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY);
    const isDesignIteration1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1);
    const isAppEditionSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_APP_EDITION_SUPPORT);
    const isTWCPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2);

    const { data: linkedEntitlementsData, error: linkedEntitlementError } = useFetchLinkedEntitlementsPPCQuery({
      entitlementId: entitlement.id,
      containerId: getParentContainerId(entitlement),
      currentEntitlementType: currentEntitlementType(entitlement),
      destinationEntitlementType: [
        LinkedEntitlementTypes.PROD_ENTITLEMENT_APP,
        LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT,
        LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT,
        LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT,
        LinkedEntitlementTypes.COLLECTION_INSTANCE,
      ],
      productType: entitlement.productType as LinkedProductTypes,
      findApps: true,
      findProducts: true,
      transactionAccountId: entitlement.transactionAccountId,
      // only fetch linked entitlements data if product has no billing group id
      // and need to be assigned a new billing group id with bill coupling to comply
      skipOption: !!entitlement.billingGroupId,
      query: fetchLinkedEntitlementBillingProfile,
    });

    const {
      collectionDataLoading,
      linkedSubscriptions,
      collectionDataError,
      getPayloadForLinkedCollectionProduct,
    } = useChangeSubscriptionForCollectionProduct({ entitlement });

    React.useEffect(() => {
      /**
       * In order to add invoice group automatically selected for entitlement in case of linked products available
       */
      const linkedEntitlements: Array<LinkedDestinationEntitlement> = linkedEntitlementsData?.entitlementDetails
        ?.linkedEntitlements as Array<LinkedDestinationEntitlement>;
      if (!linkedEntitlements || !linkedEntitlements.length || entitlement.billingGroupId) {
        return;
      }
      const invoiceGroupId = linkedEntitlements.find((entitlement) => !!entitlement.billingGroupId)
        ?.billingGroupId as string;
      updateFormState({
        invoiceGroupId: {
          invoiceGroup: {},
          value: invoiceGroupId,
        },
      });
    }, [linkedEntitlementsData]);

    const currentSchedulePromotions = entitlement?.scheduledChanges?.promotions?.map((promotion, index) => {
      return {
        promotion: {
          ...promotion,
          index: index.toString(),
        },
      };
    }) as Promotion[];

    const submitFormActionForOrderV2 = async (request: any) => {
      let isPreviewSuccessful = false;

      const previewResponse = await getOrderPreviewV2(request);
      const items: OrderItemResponse[] = previewResponse?.items ?? [];
      const itemsWithError = items.find((item) => !!item.errors);
      isPreviewSuccessful = !itemsWithError;

      if (isPreviewSuccessful) {
        updateEntitlementPlanV2(request);
      }
    };

    // TODO: Check the default state for the patchRemovePromotionEnabled
    const patchRemovePromotionEnabled = true; // isEnabled(ENABLE_REMOVE_PROMOTION_API);
    const { error: formError, isProcessing, submitForm, complete, data: response } = useFormSubmit<any, any>({
      submitFormAction:
        isEligibleForPremiumTrial || isOmsV2Enabled || isLoomSupportEnabled || isEligibleForAdvancedTrial
          ? submitFormActionForOrderV2
          : updateEntitlementPlan,
    });

    const selectedBillingFrequency = useMemo(() => {
      return defaultTo(
        billingFrequency,
        defaultTo(entitlement?.pricingPlan?.billingFrequency, BillingFrequency.MONTHLY)
      );
    }, [billingFrequency]);

    const userData = useApplicationContext();
    const selectedPartnerAccountData = userData?.partnerAccounts?.find(
      ({ transactionAccountId }: { transactionAccountId: string }) => transactionAccountId === txaId
    );

    const validateBillToAddress = !!selectedPartnerAccountData?.billTo;

    const { data: transactionAccountData, loading: transactionAccountDataLoading } = useQuery<
      FetchTransactionAccountForChangeSubscriptionPaneQuery,
      FetchTransactionAccountForChangeSubscriptionPaneQueryVariables
    >(fetchTransactionAccountForChangeSubscriptionPane, {
      variables: {
        id: txaId,
      },
      displayName: 'fetchTransactionAccountForChangeSubscriptionPane',
    });

    const { data: availablePromotionData, refetch: promotionsRefetch } = useQuery<
      FetchAvailablePartnerPromotionsForChangeEntitlementPpcQuery,
      FetchAvailablePartnerPromotionsForChangeEntitlementPpcQueryVariables
    >(fetchAvailablePartnerPromotionsForChangeEntitlementPPC, {
      variables: {
        id: entitlement.entitlementNumber,
        transactionAccountId: txaId,
        billingFrequency,
        intendedBillingPeriodState: isSkipTrialChecked || !!currFormState?.skipTrial ? 'PAID' : 'TRIAL',
        discountTier: selectedPartnerAccountData?.discountTier,
        programLevelGlobal: getCorrectProgramLevelGlobal(selectedPartnerAccountData),
      },
    });

    const availableInvoiceGroups = useMemo(
      () => defaultTo(transactionAccountData?.transactionAccount?.availableInvoiceGroups?.nodes, []),
      [transactionAccountData]
    );

    const currentInvoiceGroup = useMemo(() => {
      return availableInvoiceGroups?.find((invoiceGroup) => {
        return invoiceGroup?.id === entitlement.billingGroupId;
      });
    }, [entitlement, availableInvoiceGroups]);

    let filteredAvailableInvoiceGroups = availableInvoiceGroups;
    /**
     * Filter invoice groups based on the current invoice group's ship to party name
     *
     * This changes is done to show only the invoice groups that are associated with the current ship to party
     * in the dropdown list of invoice groups when the user is changing the Plan in sidebar
     */
    if (currentInvoiceGroup && currentInvoiceGroup?.shipToParty?.name) {
      const currentIGShipToPartyName = currentInvoiceGroup?.shipToParty?.name;
      filteredAvailableInvoiceGroups = availableInvoiceGroups.filter(
        (invoiceGroup) => invoiceGroup?.shipToParty?.name === currentIGShipToPartyName
      );
    }

    const isBillingCycleChanged = useMemo(() => {
      if (
        (entitlement?.pricingPlan?.billingFrequency === BillingFrequency.MONTHLY || isCurrentEntitlementFree) &&
        billingFrequency === BillingFrequency.ANNUAL &&
        !isStatusPage
      ) {
        if (!isCurrentEntitlementFree) {
          setSelectedOfferingId('');
          setIsSkipTrialChecked(true);
          setIsSkipTrialDisabled(true);
        }

        return true;
      }
      return false;
    }, [billingFrequency]);

    const defaultApiOfferingId = selectedOfferingId || entitlement?.offeringId || '';

    const getDefaultEntitlementData = async () => {
      if (entitlement) {
        try {
          setDefaultEntitlementData({
            ...defaultEntitlementData,
            loading: true,
          });
          const requestParams: EntitlementDefaultsRequest = {
            transactionAccountId: defaultTo(entitlement?.transactionAccountId, ''),
            offeringId: defaultApiOfferingId,
            billingCycle: selectedBillingFrequency,
          };
          if (entitlement?.baseProduct?.toLowerCase().includes('statuspage') && entitlement?.parentSen) {
            // is Statuspage add on, then pass the parent statuspage
            requestParams.currentEntitlementId = entitlement?.parentSen;
          } else if (entitlement?.isApp) {
            const appDependenceEntitlement = defaultTo(entitlement?.relatesFromEntitlements, []).filter(
              (relations) => relations?.relationshipType === RelationshipPathType.APP_DEPENDENCE
            );
            // is an JCA app, then pass the app parent JCA subscription
            requestParams.relatedEntitlementId = defaultTo(appDependenceEntitlement?.[0]?.entitlementId, undefined);
            requestParams.relationshipType = RelationshipPathType.APP_DEPENDENCE;
          } else {
            requestParams.currentEntitlementId = entitlement?.id;
          }
          const response = await getEntitlementDefaults(requestParams);
          setDefaultEntitlementData({
            ...defaultEntitlementData,
            loading: false,
            response: {
              ...(defaultEntitlementData?.response || {}),
              [selectedBillingFrequency]: {
                ...(defaultEntitlementData?.response?.[selectedBillingFrequency] || {}),
                [defaultApiOfferingId]: response,
              },
            },
          });
          updateFormState({
            ...currFormState,
            nextBillingAnchorTimestamp: defaultTo(get(response, 'billingAnchorTime.value'), undefined),
          });
        } catch (error) {
          setDefaultEntitlementData({
            ...defaultEntitlementData,
            loading: false,
            error, // TODO: handle this error case
          });
        }
      }
    };

    useEffect(() => {
      if (!defaultEntitlementData?.response?.[selectedBillingFrequency]?.[defaultApiOfferingId]) {
        getDefaultEntitlementData();
      }
    }, [selectedBillingFrequency, selectedOfferingId]);

    const checkIsOptingForUpgradeTrial = () => {
      const selectedOffering = availableOfferings.find((offering) => offering.id === selectedOfferingId);
      const currentOffering = entitlement.offering;

      if (selectedOffering?.name.toUpperCase() === EntitlementEdition.PREMIUM && !!entitlement.upgradeTrial) {
        setHasOptedForUpgradeTrial(false);
        setIsSkipTrialChecked(true);
        return;
      }
      if (
        !isStatusPage &&
        currentOffering?.name.toUpperCase() === EntitlementEdition.STANDARD &&
        !isTrial &&
        selectedOffering?.name.toUpperCase() === EntitlementEdition.PREMIUM &&
        !isSkipTrialChecked
      ) {
        setHasOptedForUpgradeTrial(true);
      } else {
        setHasOptedForUpgradeTrial(false);
      }
    };

    React.useEffect(() => {
      checkIsOptingForUpgradeTrial();
    }, [selectedOfferingId, isSkipTrialChecked]);

    React.useEffect(() => {
      (async () => {
        const offerings = await graphqlClient().query<FetchAvailableOfferingsForEntitlementPpcQuery>({
          query: fetchAvailableOfferingsForEntitlement,
          variables: {
            id: entitlement.id,
            transactionAccountId: txaId,
            isOfferingGroupChangesIncluded: true,
          },
          fetchPolicy: 'no-cache',
        });
        setAvailableOfferings({ ...offerings });
      })();
    }, []);

    React.useEffect(() => {
      if (availablePromotionData?.entitlementDetails?.availablePromotions) {
        if (availablePromotionData?.entitlementDetails?.availablePromotions?.length > 1) {
          setMoreThanOnePartnerPromotionsAvailable(true);
          return;
        }
        setAvailablePromotions(
          defaultTo(availablePromotionData?.entitlementDetails?.availablePromotions, []) as Promotion[]
        );
      }
    }, [availablePromotionData]);

    React.useEffect(() => {
      (async () => {
        if (!isSkipTrialDisabled) {
          const intendedBillingPeriodState = isSkipTrialChecked ? 'PAID' : 'TRIAL';
          if (!cachedPromotions[intendedBillingPeriodState]) {
            const fetchedPromotions = await promotionsRefetch({
              intendedBillingPeriodState,
            });
            setCachedPromotions((prevState: any) => {
              return {
                ...prevState,
                [intendedBillingPeriodState]: fetchedPromotions?.data,
              };
            });
          } else {
            setAvailablePromotions(
              cachedPromotions[intendedBillingPeriodState]?.entitlementDetails?.availablePromotions
            );
          }
        }
      })();
    }, [isSkipTrialDisabled, isSkipTrialChecked, cachedPromotions, promotionsRefetch]);

    const updateFormState = (updatedFormState: FormData) => {
      setCurrFormState(cloneDeep(assign(currFormState, updatedFormState)));
    };

    const { sendSuccessNotification, sendErrorNotification, notificationSent, setNotificationSent } = useNotification();
    const { closeSidebar } = useSidebar();

    const filterValidPromotion = (selectedPromo: SelectPromotionSelectItem) => {
      const promotion = selectedPromo?.promotion;
      return !!promotion?.id;
    };

    const getPlanChangeType = (
      selectedOfferingId: string | undefined,
      availableOfferings: Array<any>,
      entitlement: EntitlementDetails
    ) => {
      const selectedOffering = availableOfferings?.find((offering: Offering) => offering?.id === selectedOfferingId);
      const offering = entitlement?.offering as Offering;
      if (!offering?.level || !selectedOffering?.level || offering?.level === selectedOffering?.level) return null;
      if (selectedOffering?.level > offering?.level) {
        return PlanType.UPGRADE;
      }
      return PlanType.DOWNGRADE;
    };

    // To determine if changed offering is FREE and have pricing type FREE.
    const isImmediateAmendmentToFreeOrder = (availableOfferings: Offering[], selectedOfferingId: string): boolean => {
      return !!availableOfferings.find((offering) => {
        return (
          offering.pricingType === PricingType.FREE &&
          offering.id === selectedOfferingId &&
          offering.name.toLowerCase().includes('free')
        );
      });
    };

    const getSkipTrialRequestBody = () => {
      let skipTrial = {};
      if (
        (isExistingAnnualPlanForJCA && isTrial) || //remove this condition once we start supporting edition change for annual evergreen
        isBillingCycleChanged ||
        (productSkipTrialEligible && (!isNil(currFormState.newPlan) || !isNil(currFormState.pricePlan)))
      ) {
        skipTrial = { skipTrial: isSkipTrialChecked || !!currFormState?.skipTrial };
      }
      return skipTrial;
    };

    const getPromotionsRequestBody = () => {
      let scheduleRemovePromotions: any = [];
      let operations: PatchRequestOperation[] = [];

      const scheduledPromotionOperations: PatchRequestOperation[] = [];
      const promotionsToRemove: string[] = [];
      if (patchRemovePromotionEnabled) {
        if (
          currFormState &&
          currFormState?.promotionsToRemove &&
          currFormState?.promotionsToRemove?.length > 0 &&
          currentSchedulePromotions &&
          currentSchedulePromotions?.length > 0
        ) {
          //@ts-ignore
          currFormState.schedulePromotionsToRemove = currentSchedulePromotions;
          //@ts-ignore
          (currFormState?.promotionsToRemove || []).forEach((promo: any, index: number) => {
            scheduledPromotionOperations.push({
              op: PatchOperation.REMOVE,
              path: `/subscription/promotionInstances/${index}`,
            });
          });
        }

        let ind = -1;
        (currFormState?.schedulePromotionsToRemove || []).forEach((promo: any) => {
          if (
            activePromotions.some((activepromotion: any, index: number) => {
              ind = index;
              return activepromotion?.id === promo.promotion.id;
            })
          ) {
            scheduledPromotionOperations.push({
              op: PatchOperation.REMOVE,
              path: `/subscription/promotionInstances/${ind}`,
            });
          }
          scheduledPromotionOperations.push({
            op: PatchOperation.REMOVE,
            path: `/subscription/scheduledChanges/promotionInstances/${promo.promotion.index}`,
          });
          promotionsToRemove?.push(promo.promotion.promotionInstanceId);
        });
        operations = [...scheduledPromotionOperations];
      } else {
        scheduleRemovePromotions = (currFormState?.schedulePromotionsToRemove || []).map((promotion: any) => {
          promotionsToRemove?.push(promotion.promotion.promotionInstanceId);
          return {
            promotionDefinition: {
              promotionId: promotion.promotion.promotionInstanceId,
            },
          };
        });
      }

      const validPromoCodes: any[] = [];
      const promoCodesInFormState = Object.keys(currFormState).filter((key) => key.trim().includes('promoCode'));
      const validPromoCodesInFormState = [...promoCodesInFormState];
      // Note: Similar logic for sending only valid promo code is present for bill estimation as well
      validPromoCodesInFormState.length = validPromoCodeData?.length; // To only send valid promo code for order request

      validPromoCodesInFormState?.forEach((key: string) => {
        // Add promo code value in the appropriate object definition for order request
        let promotionToAdd;

        if (!isDiscretionaryDiscountPromotion(currFormState, key)) {
          promotionToAdd = {
            promotionInstanceId: null,
            promotionDefinition: {
              promotionId: null,
              promotionCode: null,
              customisedValues: null,
            },
          };

          // @ts-ignore
          promotionToAdd.promotionDefinition.promotionCode = currFormState[key];
        }

        promotionToAdd && validPromoCodes.push(promotionToAdd);
      });

      const filteredPromotionsToAdd = validPromoCodes.filter(
        (promotion) =>
          promotion.promotionDefinition.promotionId ||
          promotion.promotionDefinition.promotionCode ||
          promotion.promotionDefinition.customisedValues
      ); //@ts-ignore
      let removePromotions: PromotionInstance[] = defaultTo(currFormState?.promotionsToRemove, [])
        .filter(filterValidPromotion)
        .map((promotionItem) => ({
          promotionDefinition: {
            promotionId: defaultTo(promotionItem?.promotion?.id, undefined),
            customisedValues: promotionItem?.promotion?.isPromotionCustomisable
              ? {
                  applicationReason: {
                    id: defaultTo(promotionItem?.promotion?.applicationReason?.id, ''),
                  },
                  benefits: [
                    {
                      value: defaultTo(promotionItem?.promotion?.benefits?.[0]?.value, undefined),
                      duration: defaultTo(promotionItem?.promotion?.benefits?.[0]?.duration, ''),
                      iterations: defaultTo(promotionItem?.promotion?.benefits?.[0]?.iterations, null),
                    },
                  ],
                }
              : null,
          },
        }));
      if (scheduleRemovePromotions.length > 0) {
        removePromotions = removePromotions.concat(scheduleRemovePromotions);
      }
      let addPromotions = filteredPromotionsToAdd;

      // If a new partner promotion is available for this product, then
      // we will replace the existing partner promotion with the new partner promotion
      if (availablePromotions && availablePromotions.length > 0) {
        const availablePromotionId = availablePromotions[0].id;
        const existingPartnerPromotionIdInTheEntitlement = (entitlement?.promotions as Promotion[])?.find(
          (promotion) => promotion?.type === PARTNER_DISCOUNT_TYPE
        )?.id;
        const isPartnerDiscountToAddOrRemove = (() => {
          if (isOmsV2Enabled || isLoomSupportEnabled) {
            if (availablePromotionId && existingPartnerPromotionIdInTheEntitlement) {
              // If there is an existing partner promotion in the entitlement, then we will replace it with the new partner promotion
              return availablePromotionId !== existingPartnerPromotionIdInTheEntitlement;
            }
            // If there is no existing partner promotion in the entitlement, then we will add the new partner promotion
            return !existingPartnerPromotionIdInTheEntitlement;
          }
          // If OMS V2 is not enabled, then we will always add the new partner promotion and remove the existing partner promotion if it exists
          return true;
        })();

        addPromotions = [
          ...addPromotions,
          ...(isPartnerDiscountToAddOrRemove
            ? [
                {
                  promotionDefinition: {
                    promotionId: availablePromotionId,
                  },
                },
              ]
            : []),
        ];

        if (existingPartnerPromotionIdInTheEntitlement) {
          removePromotions = [
            ...removePromotions,
            ...(isPartnerDiscountToAddOrRemove
              ? [
                  {
                    promotionDefinition: {
                      promotionId: existingPartnerPromotionIdInTheEntitlement,
                    },
                  },
                ]
              : []),
          ];
        }
      }

      return {
        addPromotions,
        promotionsToRemove,
        operations,
        removePromotions,
      };
    };

    const getPricingPlanRequestBody = () => {
      const pricingPlanDetails: any = {};
      if (!hasOptedForUpgradeTrial && entitlement.upgradeTrial) {
        pricingPlanDetails.pricingPlanId = entitlement.upgradeTrial.pricingPlan?.id;
        pricingPlanDetails.chargeType = entitlement.upgradeTrial.pricingPlan?.chargeType as string;
      }

      if (currFormState?.pricePlan) {
        pricingPlanDetails.pricingPlanId = currFormState?.pricePlan?.value;
        pricingPlanDetails.chargeElement = currFormState?.pricePlan?.chargeElement;
        pricingPlanDetails.chargeType = currFormState?.pricePlan?.chargeType;
        pricingPlanDetails.pricingPlanType = currFormState?.pricePlan?.pricingPlanType as CCPPricing;
      }

      return pricingPlanDetails;
    };

    const buildRequestForOrderV2 = (formData: FormData) => {
      const requestOfferingId = defaultTo(currFormState?.newPlan?.value, entitlement?.offering?.id);
      const requestPricingId = defaultTo(pricingPlanId, entitlement.pricingPlan?.id);
      // Indicates user is moving to PAID premium in this case premium pricingPlan ID should be send
      // Currently, there is no form field for billing frequency, hence defaulting it to the state
      const requestBillingFrequency = currFormState?.billingFrequency ?? billingFrequency;
      const requestChargeQuantity = (currFormState?.userTier?.value ||
        currFormState?.units ||
        entitlement?.unitCount ||
        1) as number;
      const requestSkipTrialObject = getSkipTrialRequestBody();

      let request: CustomOrderEntitlement = {
        entitlementId: entitlement.id,
        orderType: OrderItemType.AMENDMENT_ORDER,
        productId: entitlement.productId as string,
        offeringId: requestOfferingId,
        pricingPlanId: requestPricingId,
        pricingPlanType: defaultPricingPlan as CCPPricing,
        chargeQuantity: (currFormState?.userTier?.value ||
          currFormState?.units ||
          entitlement?.unitCount ||
          1) as number,
        chargeElement: getChargeElement(entitlement.productId as string),
        behaviourAtEndOfTrial: get(
          defaultEntitlementData,
          `response.${requestBillingFrequency}.${requestOfferingId}.behaviourAtEndOfTrial.value`,
          null
        ),
        isImmediateAmendmentToFree: isImmediateAmendmentToFreeOrder(availableOfferings, selectedOfferingId as string),
        isExpiredPricingPlan: notActivePricingStatus.includes(entitlement?.pricingPlan?.status as string),
        ...(currFormState?.nextBillingAnchorTimestamp && {
          nextBillingAnchorTimestamp: currFormState?.nextBillingAnchorTimestamp,
        }),
      };

      if (isEligibleForPremiumTrial || isEligibleForAdvancedTrial) {
        request = {
          ...request,
          chargeQuantity: get(
            defaultEntitlementData,
            `response.${requestBillingFrequency}.${entitlement?.offering?.id}.chargeQuantities[0].quantity`,
            null
          ),
          chargeElement: get(
            defaultEntitlementData,
            `response.${requestBillingFrequency}.${entitlement?.offering?.id}.chargeQuantities[0].chargeElement`,
            null
          ),
          chargeType: 'LICENSED',
          skipTrial: false,
          addPromotions: [],
          operations: [],
          promotionsToRemove: [],
          removePromotions: [],
          isImmediateAmendmentToFree: false,
          behaviourAtEndOfTrial: BehaviourAtEndOfTrial.REVERT_TRIAL,
        };
      } else {
        request = { ...request, ...getPricingPlanRequestBody() };
        request = { ...request, ...requestSkipTrialObject };
        request = { ...request, ...getPromotionsRequestBody() };
      }

      const entitlementsList: CustomOrderEntitlement[] = [];
      entitlementsList.push(request);

      if (isCollectionProductEntitlement(entitlement?.productId)) {
        const linkedEntitlementRequest = getPayloadForLinkedCollectionProduct({
          requestOfferingId,
          requestChargeQuantity,
          requestSkipTrial: get(requestSkipTrialObject, 'skipTrial', false),
        });
        entitlementsList.push(...linkedEntitlementRequest);
      }

      const requestPayloadV2 = {
        invoiceGroupId: defaultTo(formData?.invoiceGroupId?.value, ''),
        billingFrequency: requestBillingFrequency,
        transactionAccountId: defaultTo(entitlement?.transactionAccountId, ''),
        entitlements: entitlementsList,
        ...(formData?.notes && { metadata: { [OMSMetadata.HAND_NOTE]: formData?.notes } }),
      };
      return requestPayloadV2;
    };

    const buildRequest = (formData: FormData) => {
      // Currently, there is no form field for billing frequency, hence defaulting it to the state
      const requestBillingFrequency = currFormState?.billingFrequency ?? billingFrequency;
      const requestOfferingId = defaultTo(currFormState?.newPlan?.value, entitlement?.offering?.id);

      let request: UpdateOrderRequest = {
        invoiceGroupId: defaultTo(formData?.invoiceGroupId?.value, ''),
        billingFrequency: requestBillingFrequency,
        isBillingCycleChanged,
        entitlementId: entitlement.id,
        units: currFormState?.userTier?.value || currFormState?.units || '1',
        isPromotionStackingAllowed: true,
        offeringId: requestOfferingId,
        orderType: OrderType.AMENDMENT_ORDER,
        productId: entitlement.productId || '', //@ts-ignore
        chargeQuantities: entitlement?.scheduledChanges?.chargeQuantities || [],
        pricingPlanType: defaultPricingPlan,
        chargeElement: getChargeElement(entitlement?.productId as string),
        // Indicates an upgradeTrial order . Backend OMS engine utilizes this flag to identify if scheduled promotions should be added or not .
        isUpgradeTrialOrder: hasOptedForUpgradeTrial || !!entitlement.upgradeTrial,
        isImmediateAmendmentToFree: isImmediateAmendmentToFreeOrder(availableOfferings, selectedOfferingId as string),
        behaviourAtEndOfTrial: get(
          defaultEntitlementData,
          `response.${requestBillingFrequency}.${requestOfferingId}.behaviourAtEndOfTrial.value`,
          null
        ),
        // To check that the pricing plan status while amending an order
        isExpiredPricingPlan: notActivePricingStatus.includes(entitlement?.pricingPlan?.status as string),
        ...getPricingPlanRequestBody(),
        ...getSkipTrialRequestBody(),
        ...getPromotionsRequestBody(),
      };
      if (currFormState?.nextBillingAnchorTimestamp) {
        request.nextBillingAnchorTimestamp = `${currFormState?.nextBillingAnchorTimestamp}`;
      }
      request = {
        ...request,
        metadata: {
          [OMSMetadata.HAND_NOTE]: defaultTo(formData?.notes, null),
        },
      };

      return request;
    };

    const calculateAndSendTimeMetric = () => {
      logTimeStamp(new Date().getTime(), 'Change Subscription', SignalFxFEPerfStore.FE_USER_TASK_CHANGE_SUB_TIME);
    };

    const isManagePlanFormDirty = useMemo(() => {
      // for apps we are allowing the form to go to Discounts step, that's why allowing it
      if (currentProgressIndex !== FormTrackerStep.PLAN || entitlement?.isApp) {
        return true;
      }
      if (currFormState.newPlan) {
        return true;
      }
      // For JCA Products and statuspage
      if (currFormState.skipTrial || currFormState.units) {
        return true;
      }
      return false;
    }, [currentProgressIndex, currFormState]);

    const sendChangeFailedTrackEvent = () => {
      analytics.sendTrackEvent({
        action: AnalyticsTrackAction.FAILED,
        actionSubject: AnalyticsTrackSubject.CHANGE_PLAN,
        attributes: {
          entitlementId: entitlement.id,
        },
      });
    };

    const handleFormSubmit = async (formData: FormData) => {
      setIsBillingProfileInValid(false);

      if (currentProgressIndex < progressTrackerItems.length - 1) {
        setCurrFormState(cloneDeep(assign(currFormState, formData)));
        setCurrentProgressIndex(currentProgressIndex + 1);
      } else {
        calculateAndSendTimeMetric();

        if (
          (grandTotal ?? 0) > 0 &&
          (!validateBillToAddress || !defaultTo(formData?.invoiceGroupId?.invoiceGroup?.defaultPaymentMethod, null))
        ) {
          setIsBillingProfileInValid(true);
          return;
        }

        const request =
          isEligibleForPremiumTrial || isOmsV2Enabled || isLoomSupportEnabled || isEligibleForAdvancedTrial
            ? buildRequestForOrderV2(formData)
            : buildRequest(formData);
        try {
          const content = (Array.from(document.querySelectorAll('.js-consent-hub-text')) ?? []).reduce(
            (finalString, currentNode) => {
              finalString += currentNode.textContent;
              return finalString;
            },
            ''
          );
          await updateCustomerConsent(content);
          submitForm(request);
        } catch {
          const errorNotificationOptions = errorNotificationBuilder()
            .isVersion(NotificationTypes.FLAG)
            .withId('change-subscription-error')
            .withHeader("Sorry, we couldn't change the subscription.")
            .withMessage('Some error has occurred')
            .withDismissAfter(5000)
            .build();
          sendChangeFailedTrackEvent();
          sendErrorNotification(errorNotificationOptions);
          setNotificationSent(true);
        }
      }
    };

    const isSubmitButtonDisabled = useCallback(() => {
      const isReviewStep = !isDesignIteration1Enabled || currentProgressIndex === FormTrackerStep.REVIEW;
      return (
        (isReviewStep && isCurrentEntitlementFree && !selectedOfferingId && isBillingCycleChanged) ||
        availableOfferingsData.loading ||
        defaultEntitlementData.loading ||
        discretionaryEstimate.loading ||
        (isReviewStep && showDiscretionaryEstimateError) ||
        discretionaryEstimate?.isDisabled ||
        !isManagePlanFormDirty
      );
    }, [
      availableOfferingsData.loading,
      discretionaryEstimate,
      defaultEntitlementData.loading,
      selectedOfferingId,
      isBillingCycleChanged,
      showDiscretionaryEstimateError,
      currentProgressIndex,
    ]);

    const availableOfferings = useMemo(() => {
      const allAvailableOfferings = availableOfferingsData?.data?.entitlementDetails?.availableOfferings || [];
      const currentOffering = availableOfferingsData?.data?.entitlementDetails?.offering;

      // TODO :: commenting this until the bill estimate starts giving right data
      //  https://hello.atlassian.net/browse/ITFO-9724
      // // for trial case we are appending the current offering as well to give an option to select
      // // paid subscription
      // if (isTrial && currentOffering) {
      //   allAvailableOfferings = [...allAvailableOfferings, currentOffering];
      // }

      return allAvailableOfferings.filter((offering) => {
        const offeringName = offering?.name?.toLowerCase() || '';

        // hide premium option when subscription is on premium upgrade trial for JCA products as
        // bill estimate information is not available from OMS side
        if (
          !isStatusPage &&
          currentOffering?.name.toUpperCase() === EntitlementEdition.STANDARD &&
          !isTrial &&
          !!entitlement.upgradeTrial &&
          offering?.name.toUpperCase() === EntitlementEdition.PREMIUM
        ) {
          return false;
        }

        return !offeringName.includes('sandbox');
      });
    }, [
      availableOfferingsData?.data?.entitlementDetails?.availableOfferings,
      availableOfferingsData?.data?.entitlementDetails?.offering,
      isTrial,
      isStatusPage,
      entitlement.upgradeTrial,
    ]) as Offering[];

    if (complete && !notificationSent) {
      const notificationConfig = buildMessage({
        response,
        isDestinationTrial: !currFormState?.skipTrial,
        isDestinationFreeEdition: (currFormState?.newPlan?.label ?? '').toUpperCase() === EntitlementEdition.FREE,
      });
      const notificationOptions = successNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withId('change-subscription-success')
        .withHeader(notificationConfig.header)
        .withMessage(notificationConfig.message)
        .withDismissAfter(5000)
        .build();
      sendSuccessNotification(notificationOptions);
      setNotificationSent(true);
      const selectedOfferingId = defaultTo(currFormState?.newPlan?.value, entitlement?.offering?.id);
      analytics.sendTrackEvent({
        attributes: {
          changeType: getPlanChangeType(selectedOfferingId, availableOfferings, entitlement),
          entitlementId: entitlement.id,
        },
        action: AnalyticsTrackAction.UPDATED,
        actionSubject: AnalyticsTrackSubject.CHANGE_PLAN,
      });
      closeSidebar();
      refetch();
    }

    const stages = entitlement?.isApp ? stagesForApp : stagesForProduct;

    const progressTrackerItems: Stages = useMemo(() => {
      //show progress tracker without label and precentage for two stages
      if (isDesignIteration1Enabled && stages.length === 2) {
        return stages.map((_, index) => ({
          id: `${index}`,
          label: '',
          percentageComplete: 0,
          status: currentProgressIndex === index ? TrackerState.CURRENT : TrackerState.UNVISITED,
        }));
      }

      return stages.map((stage, index) => ({
        id: `${index}`,
        label: stage,
        percentageComplete: currentProgressIndex - 1 < index ? 0 : 100,
        status:
          currentProgressIndex > index
            ? TrackerState.VISITED
            : currentProgressIndex === index
            ? TrackerState.CURRENT
            : TrackerState.UNVISITED,
        onClick: () => setCurrentProgressIndex(index),
      }));
    }, [currentProgressIndex]);

    const selectedOfferingInForm = availableOfferings.find(
      (availableOffering) => availableOffering.id === currFormState?.newPlan?.value
    );
    const isNewPlanFree = selectedOfferingInForm?.pricingType === 'FREE';
    const isPaidToFreeTransition = !isCurrentEntitlementFree && isNewPlanFree;
    const isTrialToTrialConversion = isTrial && !isSkipTrialChecked;
    const offeringName = entitlement?.offering?.name?.toUpperCase();
    const selectedOfferingName = selectedOfferingInForm?.name?.toUpperCase();
    const isPremiumToStandardTransition =
      offeringName === EntitlementEdition.PREMIUM && selectedOfferingName === EntitlementEdition.STANDARD;
    const isAppEditionDowngrade =
      entitlement?.offering?.level &&
      selectedOfferingInForm?.level &&
      entitlement?.offering?.level > selectedOfferingInForm?.level;

    // we are suppressing the bill estimate related error because Stripe has no Information for trial entitlements
    // we are showing the error only on the review step
    showDiscretionaryEstimateError =
      currentProgressIndex === FormTrackerStep.REVIEW && !!discretionaryEstimate?.error && !isTrialToTrialConversion;
    const renderFormStep = ({ entitlement }: { entitlement: EntitlementDetails }) => {
      switch (currentProgressIndex) {
        case FormTrackerStep.PLAN: {
          if (availableOfferingsData.loading || transactionAccountDataLoading || collectionDataLoading) {
            return <LoadingIndicator />;
          }
          return (
            <ManagePlan
              availableOfferings={availableOfferings}
              formState={currFormState}
              entitlement={entitlement}
              availableInvoiceGroups={availableInvoiceGroups}
              isSkipTrialChecked={isSkipTrialChecked}
              isSkipTrialDisabled={isSkipTrialDisabled}
              onSkipTrialChange={setIsSkipTrialChecked}
              onSkipTrialDisableChange={setIsSkipTrialDisabled}
              productSkipTrialEligible={productSkipTrialEligible}
              setProductSkipTrialEligible={setProductSkipTrialEligible}
              isImmediateFlag={isImmediateFlag}
              onIsImmediateFlagChange={setIsImmediateFlag}
              evaluatePricingPlan={evaluatePricingPlan}
              onSelectOfferingId={setSelectedOfferingId}
              setBillingFrequency={setBillingFrequency}
              billingFrequency={billingFrequency}
              isOnUpgradeTrial={hasOptedForUpgradeTrial}
              isBillingCycleChanged={isBillingCycleChanged}
              isCurrentEntitlementFree={isCurrentEntitlementFree}
              isExistingAnnualPlanForJCA={isExistingAnnualPlanForJCA}
              updateFormState={updateFormState}
              showSkipTrialCheckbox={showSkipTrialCheckbox}
              billToPartyPricingEligibilty={transactionAccountData?.transactionAccount?.billToPartyPricingEligibilty}
              isCompassPremiumEnabled={isCompassPremiumEnabled}
              isJPDPremiumOfferingEnabled={isJPDPremiumOfferingEnabled}
              isJPDAsJiraFamilyEnabled={isJPDAsJiraFamilyEnabled}
              isDesignIteration1Enabled={isDesignIteration1Enabled}
              isTWCPhase2Enabled={isTWCPhase2Enabled}
              isAppEditionSupportEnabled={isAppEditionSupportEnabled}
              linkedSubscriptions={linkedSubscriptions}
            />
          );
        }
        case FormTrackerStep?.PROMOTIONS:
          return (
            <ManagePromotion
              formState={currFormState}
              entitlement={entitlement}
              updateFormState={updateFormState}
              validPromoCodeData={validPromoCodeData}
              setValidPromoCodeData={setValidPromoCodeData}
              activePromoCodeData={activePromoCodeData}
              setActivePromoCodeData={setActivePromoCodeData}
              isDesignIteration1Enabled={isDesignIteration1Enabled}
            />
          );
        case FormTrackerStep.REVIEW: {
          return (
            <ReviewChange
              availableOfferings={availableOfferings}
              availableInvoiceGroups={filteredAvailableInvoiceGroups}
              formState={currFormState}
              entitlement={entitlement}
              isSkipTrialChecked={isSkipTrialChecked}
              onDiscretionaryEstimateChange={setDiscretionaryEstimate}
              setGrandTotal={setGrandTotal}
              isBillingCycleChanged={isBillingCycleChanged}
              isExistingAnnualPlanForJCA={isExistingAnnualPlanForJCA}
              isOfferingChanged={!!selectedOfferingId && selectedOfferingId !== entitlement?.offeringId}
              setCurrentProgressIndex={setCurrentProgressIndex}
              isCurrentEntitlementFree={isCurrentEntitlementFree}
              validPromoCodeData={validPromoCodeData}
              isPaidToFreeTransition={isPaidToFreeTransition}
              isPremiumToStandardTransition={isPremiumToStandardTransition}
              availablePartnerPromotion={availablePromotions?.[0]}
              billToPartyPricingEligibilty={transactionAccountData?.transactionAccount?.billToPartyPricingEligibilty}
              updateFormState={updateFormState}
              txaId={txaId}
              isSearchable={isEnabled(STATSIG_FEATURE_GATES.ENABLE_BILLING_PROFILE_SEARCH)}
              isEligibleForPremiumTrial={isEligibleForPremiumTrial}
              defaultEntitlementData={defaultEntitlementData}
              setPricingPlanId={setPricingPlanId}
              isAppEditionDowngrade={!!isAppEditionDowngrade}
              isEligibleForAdvancedTrial={isEligibleForAdvancedTrial}
            />
          );
        }
      }
    };

    const hasPromotions = () => {
      return !!(entitlement.promotions?.length || entitlement.scheduledChanges?.promotions?.length);
    };

    const [isAppArchivedWarningShown, isAppAtNoticeWarningShown] = React.useMemo(() => {
      if (!entitlement?.isApp) return [false, false];
      if (selectedOfferingId) {
        if (selectedOfferingId === entitlement?.offeringId) {
          return [isOfferingArchived(entitlement), isOfferingAtNotice(entitlement)];
        }
        const selectedOffering = availableOfferings?.find((offering: Offering) => offering?.id === selectedOfferingId);
        return [
          selectedOffering?.status === OfferingStatus.EXPIRED,
          selectedOffering?.status === OfferingStatus.AT_NOTICE,
        ];
      }
      return [isOfferingArchived(entitlement), isOfferingAtNotice(entitlement)];
    }, [selectedOfferingId, entitlement]);

    const getTermsAndConditions = () => {
      const agreementConfirmationText = () => (
        <>
          By confirming the order, and in accordance with the order section of the partner agreement executed between
          You and Atlassian, you agree and acknowledge to be bound by the partner agreement, and acknowledge the{' '}
          <a href={legalLinks.privacyPolicyUrl} target="_blank">
            Privacy Policy.
          </a>
        </>
      );

      const ensureCustomerAgreementCompliance = () => (
        <>
          You are responsible for ensuring each customer has entered into a customer agreement, at or before such
          customer&apos;s purchase or use of the offerings, in a manner that is legally binding upon the customer.
        </>
      );

      const agreementConflictResolutionText = () => (
        <>
          Except where you have a current agreement with Atlassian, the terms of such agreement shall supersede the{' '}
          <a href={legalLinks.customerAgreementUrl} target="_blank">
            Atlassian Customer Agreement.
          </a>{' '}
          In the event of any conflict between the terms of your current agreement and the{' '}
          <a href={legalLinks.customerAgreementUrl} target="_blank">
            Atlassian Customer Agreement
          </a>
          , the terms of your current agreement shall prevail.
        </>
      );

      const acknowledgementText = () => (
        <>
          <p className="mt-8">
            You acknowledge that your order contains monthly auto-renewal subscriptions. You agree to the details,
            price, frequency, and other adjustments presented here, and understand that if your customer adjusts the
            number of users, the price will be adjusted accordingly. On the next bill date and after any trial periods,
            we&apos;ll invoice you with the final amount. You may first be charged a prorated amount to align your bill
            date with other subscriptions.
          </p>
          <p className="mt-8">
            Our prices may change upon notice to you. Your customer contact can cancel the subscription anytime by going
            to{' '}
            <a
              data-testid="atlassian-pms-link"
              href={getPartnerSubscriptionsUrl()}
              target="_blank"
              rel="noreferrer"
              onClick={trackPartnerServiceDeskClick}
            >
              admin.atlassian.com/partner-managed-subscriptions
            </a>{' '}
            and following the steps.
          </p>
        </>
      );

      return [
        () => (
          <>
            <p className="mt-8">
              {agreementConfirmationText()} {agreementConflictResolutionText()}
            </p>
            <p className="mt-8">{ensureCustomerAgreementCompliance()}</p>
          </>
        ),
        () => {
          return acknowledgementText();
        },
      ];
    };

    if ((linkedEntitlementError || collectionDataError) && !notificationSent) {
      const errorNotificationOptions = errorNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withId('linked-subscriptions-error')
        .withHeader('Sorry, we are not able to fetch the linked subscriptions')
        .withMessage('Some error has occurred')
        .withDismissAfter(5000)
        .build();
      sendChangeFailedTrackEvent();
      sendErrorNotification(errorNotificationOptions);
      setNotificationSent(true);
    }

    // to select the premium plan by default if eligible for premium trial
    useEffect(() => {
      const activeOfferingOptions = getAvailableActiveOfferingOptions(
        availableOfferings,
        isCompassPremiumEnabled,
        isJPDPremiumOfferingEnabled,
        isAppEditionSupportEnabled
      );
      const premiumPlan = activeOfferingOptions.find(
        (offering) => offering.label.toUpperCase() === EntitlementEdition.PREMIUM
      );

      const advancedPlan = activeOfferingOptions?.find((offering) => offering.label.toUpperCase() === 'ADVANCED');
      if ((isEligibleForPremiumTrial && premiumPlan) || (isEligibleForAdvancedTrial && advancedPlan)) {
        setCurrFormState(
          cloneDeep(
            assign(currFormState, {
              newPlan: entitlement?.isApp ? advancedPlan : premiumPlan,
              userTier: { label: getUserTier(entitlement?.subscription) },
            })
          )
        );
      }
    }, [
      availableOfferings,
      isEligibleForPremiumTrial,
      entitlement?.subscription,
      isCompassPremiumEnabled,
      isJPDPremiumOfferingEnabled,
    ]);

    const renderFooterProgressTracker = () => {
      if (!isDesignIteration1Enabled) {
        return null;
      }

      //Show progress tracker in the footer for the two stages.
      if (stages.length !== 2) {
        return null;
      }

      return (
        <div className="footer-progress-content">
          <ProgressTracker items={progressTrackerItems} spacing="compact" />
        </div>
      );
    };

    const getConfirmButtonText = () => {
      if (currentProgressIndex === FormTrackerStep.REVIEW) {
        return 'Confirm';
      }
      return isDesignIteration1Enabled ? 'Next' : 'Continue';
    };

    const renderFooterButtons = () => {
      //show previous button only when the current progress index is review and there are only two stages
      const showPreviousButton =
        isDesignIteration1Enabled && currentProgressIndex === FormTrackerStep.REVIEW && stages.length === 2;
      const handleClick = () => {
        if (showPreviousButton) {
          setCurrentProgressIndex(currentProgressIndex - 1);
        } else {
          closeSidebar();
        }
      };

      const getButtonText = () => {
        if (showPreviousButton) {
          return 'Previous';
        }
        return 'Cancel';
      };

      return (
        <>
          <Button appearance={isDesignIteration1Enabled ? undefined : 'subtle'} onClick={handleClick}>
            {getButtonText()}
          </Button>
          {renderFooterProgressTracker()}
          <LoadingButton
            appearance="primary"
            type="submit"
            isDisabled={
              isSubmitButtonDisabled() ||
              (currentProgressIndex === FormTrackerStep.REVIEW && !agreedToTnC) ||
              moreThanOnePartnerPromotionsAvailable ||
              eccDetailsLoading ||
              (currentProgressIndex === FormTrackerStep.REVIEW && !isContactsAvailable)
            }
            isLoading={isProcessing || defaultEntitlementData?.loading}
            testId="save-button"
          >
            {getConfirmButtonText()}
          </LoadingButton>
        </>
      );
    };

    const renderFormFooter = () => {
      if (isDesignIteration1Enabled) {
        return (
          <div className="change-subscription-form-footer">
            <FormFooter>
              <Flex>{renderFooterButtons()}</Flex>
            </FormFooter>
          </div>
        );
      }

      return (
        <FormFooter align="end">
          <ButtonGroup>{renderFooterButtons()}</ButtonGroup>
        </FormFooter>
      );
    };

    const renderProgressTracker = () => {
      //Show progress tracker at the top of the sidebar only for more than two stages.
      if (isDesignIteration1Enabled && stages.length === 2) {
        return null;
      }

      if (hideProgressTracker) {
        return null;
      }

      return (
        <div className="progress-content">
          <ProgressTracker items={progressTrackerItems} spacing="compact" />
        </div>
      );
    };

    const renderAgreementCheckbox = () => {
      if (isDesignIteration1Enabled) {
        const handleChange = () => {
          setAgreedToTnC((prevState) => !prevState);
        };

        return (
          <CheckboxField name="edit-subscription-ppc-tnc" isRequired={true}>
            {({ fieldProps }) => (
              <Checkbox
                {...fieldProps}
                isChecked={agreedToTnC}
                label={
                  <Text color="color.text.subtle" size="small">
                    You agree to the order notice and{' '}
                    <a href={legalLinks.termsOfUseUrl}>Program Term for Partner Purchasing Center</a>, and acknowledge
                    the <a href={legalLinks.privacyPolicyUrl}>Privacy Policy.</a>
                  </Text>
                }
                onChange={handleChange}
                testId="new-terms-and-conditions-checkbox"
              />
            )}
          </CheckboxField>
        );
      }
    };

    const renderTermsAndConditions = () => {
      return (
        <EditSubscriptionTermsAndConditions
          agreedToTnC={agreedToTnC}
          isConfirmationCheckboxRequired={false}
          onChange={() => {
            setAgreedToTnC((prevState) => !prevState);
          }}
          tncTexts={getTermsAndConditions()}
        />
      );
    };

    const renderOrderNotice = () => {
      if (isDesignIteration1Enabled) {
        return (
          <Box paddingInlineStart="space.100">
            <CustomPanel
              expandedHeader="Order notice"
              collapsedHeader="Order notice"
              testId="order-notice-panel"
              version={3}
            >
              {renderTermsAndConditions()}
            </CustomPanel>
          </Box>
        );
      }

      return <>{renderTermsAndConditions()}</>;
    };

    return (
      <div
        className={classNames({
          'change-subscription-pane-form': isDesignIteration1Enabled,
        })}
      >
        <Form onSubmit={handleFormSubmit}>
          {({ formProps }) => (
            <form {...formProps}>
              <div className="header">
                <FormHeader title="Change subscription"></FormHeader>
              </div>
              {currentProgressIndex === FormTrackerStep?.PROMOTIONS && hasOptedForUpgradeTrial && (
                <FormSection>
                  {hasPromotions() ? (
                    <SectionMessage appearance="warning" testId="upgrade-trial-with-discount-banner">
                      <p>No discounts can be added or removed to the base plan during an ongoing trial.</p>
                    </SectionMessage>
                  ) : (
                    <SectionMessage appearance="warning" testId="upgrade-trial-without-discount-banner">
                      <p>No discounts can be added during an ongoing trial.</p>
                    </SectionMessage>
                  )}
                </FormSection>
              )}
              <FormSection>
                {isAppArchivedWarningShown && (
                  <InfoPanel
                    type="warning"
                    title="This app has been archived"
                    message="The subscription can only be moved to an active offering."
                  />
                )}
              </FormSection>
              <FormSection>
                {isAppAtNoticeWarningShown && (
                  <InfoPanel
                    type="warning"
                    title="This offering is in the process of being archived"
                    message="Keep in mind that any changes will only apply until the subscription end date, unless a new plan is selected."
                  />
                )}
              </FormSection>
              <FormSection>
                {showDiscretionaryEstimateError && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to process the bill estimation for the following reason"
                        message={discretionaryEstimate?.error?.message}
                        helpContent={<HelpContent />}
                      />
                    }
                  />
                )}
              </FormSection>
              <FormSection>
                {currentProgressIndex === FormTrackerStep.REVIEW &&
                  currFormState?.invoiceGroupId?.value &&
                  !eccDetailsLoading &&
                  !isContactsAvailable && (
                    <InfoPanel
                      type="error"
                      message={
                        <ErrorComponents.ErrorMessageOnInfoPanel
                          title="We're unable to proceed for the following reason"
                          message={`There is no customer contact linked to the chosen billing profile`}
                          helpContent={<HelpContent />}
                        />
                      }
                    />
                  )}
              </FormSection>
              <FormSection>
                {isBillingProfileInValid && (
                  <SectionMessage
                    appearance="error"
                    title={
                      !validateBillToAddress && !currentInvoiceGroup?.defaultPaymentMethod
                        ? 'Update billing account and billing profile'
                        : !validateBillToAddress
                        ? 'Update billing account'
                        : 'Update billing profile'
                    }
                    testId="invalid-billing-profile"
                  >
                    <div>
                      <p>Subscription changes can’t be made because there’s:</p>
                      <br />
                      <ul>
                        {!validateBillToAddress ? <li>No bill-to address available for the billing account</li> : null}

                        {!currentInvoiceGroup?.defaultPaymentMethod ? (
                          <li>No payment method available for the billing profile</li>
                        ) : null}
                      </ul>
                      <br />
                      <p>
                        {`Add the information${validateBillToAddress ? ' or switch the billing profile' : ''}`}, then
                        try again. {<HelpContent />}
                      </p>
                    </div>
                  </SectionMessage>
                )}
              </FormSection>

              <FormSection>
                {currentInvoiceGroup?.id && (
                  <InflightInvoiceBannerForInvoiceGroup
                    invoiceGroupId={currentInvoiceGroup?.id}
                    message={DEFAULT_INFLIGHT_BANNER_MESSAGE}
                  />
                )}
              </FormSection>

              <FormSection>
                {moreThanOnePartnerPromotionsAvailable && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to proceed for the following reason"
                        message={'More than one partner discounts are available.'}
                        helpContent={<HelpContent />}
                      />
                    }
                  />
                )}
              </FormSection>
              {renderProgressTracker()}
              <FormSection>
                {formError && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to create transaction account for the following reason"
                        message={getFormError(formError, OrderType.AMENDMENT_ORDER)}
                        helpContent={<HelpContent />}
                      />
                    }
                  />
                )}
              </FormSection>
              <FormSection>{renderFormStep({ entitlement })}</FormSection>
              {currentProgressIndex === FormTrackerStep.REVIEW && (
                <FormSection>
                  <Divider />
                  {renderAgreementCheckbox()}
                  {renderOrderNotice()}
                </FormSection>
              )}
              {renderFormFooter()}
            </form>
          )}
        </Form>
      </div>
    );
  }
);
